import React from "react";
import { Box, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import type { Status as StatusId } from "api/types/aba";
import getStatusStyles from "./getStatusColor";

export const STATUSES = {
  "in-progress": { id: "inProgress", label: "In-progress" },
  "not-active": { id: "notActive", label: "Not Active" },
  discontinued: { id: "discontinued", label: "Discontinued" },
  met: { id: "met", label: "Met" },
  "on-hold": { id: "onHold", label: "On Hold" },
  archived: { id: "archived", label: "Archived" },
} as const;

export interface StatusPillProps {
  /** The status id to display */
  status: StatusId;
  /** Additional MUI sx styles */
  sx?: SxProps<Theme>;
}

/**
 * Displays a colored pill chip representing the status of a goal or target.
 * Each status has a distinct dot and text color for quick visual identification.
 *
 * @param props
 * @param props.status - The status id (key of STATUSES)
 * @param [props.sx] - Additional MUI sx styles
 */
const StatusPill: React.FC<StatusPillProps> = ({ status, sx }) => {
  return (
    <Box
      component="span"
      borderRadius="100px"
      display="inline-flex"
      px={3}
      py={"2px"}
      sx={[
        (theme) => {
          // Get the colors for the current status from the theme-aware function
          const { bgcolor, dotColor, textColor } = getStatusStyles(theme)[status];

          return {
            alignItems: "center",
            gap: "6px",
            bgcolor,
            overflow: "hidden",
            "& .StatusPill-dot": {
              bgcolor: dotColor,
            },
            "& .StatusPill-label": {
              color: textColor,
            },
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        component="span"
        className="StatusPill-dot"
        aria-hidden="true"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
        }}
      />
      <Typography component="span" className="StatusPill-label" variant="caption">
        {STATUSES[status]?.label}
      </Typography>
    </Box>
  );
};

export default StatusPill;
